import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getUserRoleList(
    $filter: userRoleFilterInput!
    $sort: [sortInput]
    $pagination: paginationInput
  ) {
    getUserRoleList(filter: $filter, sort: $sort, pagination: $pagination) {
      items {
        organization {
          name
        }
        systemUser {
          id
          name
          username
          roleList {
            id
            name
          }
        }
        organizationId
        photo
        mobile
        email
        code
        name
        id
      }
      pagination {
        pageLimit
        pageNumber
        totalCount
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.UserListState = {
  result: null,
  error: null,
  loading: false
};

export const getUserRoleListRequest = createAsyncThunk<
  TYPES.UserList,
  {
    filter: TYPES.UserRoleFilterInput;
    sort?: TYPES.SortInput[];
    pagination?: TYPES.PaginationInput;
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'user/get_user_role_list',
  async (
  params: {
    filter: TYPES.UserRoleFilterInput;
    sort?: TYPES.SortInput[];
    pagination?: TYPES.PaginationInput;
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getUserRoleList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getUserRoleListSlice = createSlice({
  name: 'get_user_role_list',
  initialState: initialState,
  reducers: {
    getUserRoleListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getUserRoleListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getUserRoleListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getUserRoleListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getUserRoleListClear } = getUserRoleListSlice.actions;

export const getUserRoleList = createSelector(
  (state: RootState) => state.user.getUserRoleList.result,
  (state: RootState) => state.user.getUserRoleList.error,
  (state: RootState) => state.user.getUserRoleList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getUserRoleListSlice.reducer;